import React, { useState, useEffect, useCallback } from 'react';
import { User, Location, TravelCost } from '../../types';
import { travelService } from '../../services/travelService';
import { drugService } from '../../services/drugService';
import { useError } from '../../contexts/ErrorContext';

interface TravelProps {
  user: User;
  onUpdate: () => void;
}

const Travel: React.FC<TravelProps> = ({ user, onUpdate }) => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [travelCosts, setTravelCosts] = useState<{ [key: string]: TravelCost }>({});
  const [loading, setLoading] = useState(false);
  const { showError, showSuccess } = useError();

  useEffect(() => {
    loadLocations();
  }, []);

  const loadTravelCosts = useCallback(async () => {
    try {
      const costs: { [key: string]: TravelCost } = {};
      
      for (const location of locations) {
        if (location.id !== user.location) {
          try {
            const costData = await drugService.getTravelCost(location.id);
            if (costData.success) {
              costs[location.id] = {
                destination: location.id,
                cost: costData.cost,
                timeMinutes: costData.timeMinutes,
                riskLevel: costData.riskLevel as 'Low' | 'Medium' | 'High'
              };
            }
          } catch (error) {
            console.error(`Error loading travel cost for ${location.id}:`, error);
            // Fallback to default costs if API fails
            costs[location.id] = {
              destination: location.id,
              cost: 100,
              timeMinutes: 3,
              riskLevel: 'Low'
            };
          }
        }
      }
      
      setTravelCosts(costs);
    } catch (error) {
      console.error('Error loading travel costs:', error);
    }
  }, [user.location, locations]);

  useEffect(() => {
    if (locations.length > 0) {
      loadTravelCosts();
    }
  }, [loadTravelCosts, locations.length]);

  const loadLocations = async () => {
    try {
      const data = await travelService.getLocations();
      setLocations(data.locations);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const handleTravel = async (destination: string) => {
    setLoading(true);
    
    try {
      const result = await travelService.travelToLocation(destination);
      
      if (result.success) {
        showSuccess(result.message);
        onUpdate();
        loadTravelCosts(); // Refresh travel costs
      } else {
        showError(result.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Error traveling to location';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (riskLevel: string): string => {
    switch (riskLevel) {
      case 'Low': return 'var(--success-color)';
      case 'Medium': return 'var(--warning-color)';
      case 'High': return 'var(--error-color)';
      default: return 'var(--text-light)';
    }
  };

  const formatPrice = (price: number): string => {
    return `$${price.toLocaleString()}`;
  };

  const formatTime = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const currentLocation = locations.find(loc => loc.id === user.location);

  return (
    <div className="travel-panel">
      <h3>Travel</h3>
      
      <div className="current-location" style={{
        background: 'var(--dark-bg)',
        padding: '15px',
        borderRadius: 'var(--border-radius)',
        marginBottom: '20px',
        border: '1px solid var(--border-color)'
      }}>
        <h4>Current Location: {currentLocation?.name || 'Unknown'}</h4>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>
          {currentLocation?.description || 'Location description not available'}
        </p>
      </div>

      <div className="travel-section">
        <h4>Available Destinations</h4>
        
        {user.garageCars && user.garageCars.length > 0 ? (
          <div className="locations-table" style={{
            background: 'var(--dark-bg)',
            borderRadius: 'var(--border-radius)',
            overflow: 'hidden',
            border: '1px solid var(--border-color)'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--darker-bg)' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Location</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Danger Level</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Cost</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Time</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Risk</th>
                  <th style={{ padding: '12px', textAlign: 'center' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => {
                  const isCurrentLocation = location.id === user.location;
                  const travelCost = travelCosts[location.id];
                  
                  return (
                    <tr key={location.id} style={{
                      borderBottom: '1px solid var(--border-color)'
                    }}>
                      <td style={{ padding: '12px' }}>
                        <div>
                          <strong>{location.name}</strong>
                          <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
                            {location.description}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{
                          color: location.danger === 'Low' ? 'var(--success-color)' :
                                 location.danger === 'Medium' ? 'var(--warning-color)' : 'var(--error-color)',
                          fontWeight: 'bold'
                        }}>
                          {location.danger}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>
                        {travelCost ? formatPrice(travelCost.cost) : 'Loading...'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {travelCost ? formatTime(travelCost.timeMinutes) : 'Loading...'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {travelCost && (
                          <span style={{ 
                            color: getRiskColor(travelCost.riskLevel),
                            fontWeight: 'bold'
                          }}>
                            {travelCost.riskLevel}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleTravel(location.id)}
                          disabled={loading || isCurrentLocation}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: isCurrentLocation ? '#666' : 'var(--accent-color)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isCurrentLocation ? 'not-allowed' : 'pointer',
                            opacity: isCurrentLocation ? 0.5 : 1
                          }}
                        >
                          {isCurrentLocation ? 'Current Location' : 'Travel Here'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="no-car-message" style={{
            textAlign: 'center',
            padding: '30px',
            background: 'rgba(255, 193, 7, 0.1)',
            border: '1px solid var(--warning-color)',
            borderRadius: 'var(--border-radius)',
            color: 'var(--warning-color)'
          }}>
            <p>You need a car to travel between New York boroughs.</p>
            <p>Steal a car from the Grand Theft Auto section first!</p>
          </div>
        )}
      </div>

      <div className="info-section">
        <h3>Travel Information</h3>
        <div className="info-grid">
          <div className="info-card">
            <h4>Travel Tips</h4>
            <ul>
              <li>Travel cost and time are based on current drug prices in each location</li>
              <li>Higher drug prices = higher travel costs and longer travel times</li>
              <li>Carrying drugs while traveling increases risk of being caught by police</li>
              <li>Car health decreases slightly with each trip</li>
              <li>You cannot travel while in jail or dead</li>
              <li>Traveling takes real time - be patient!</li>
            </ul>
          </div>
          <div className="info-card">
            <h4>Borough Characteristics</h4>
            <ul>
              <li><strong>Manhattan:</strong> High crime rewards, expensive properties</li>
              <li><strong>Brooklyn:</strong> Moderate opportunities, diverse area</li>
              <li><strong>Queens:</strong> Steady income, residential area</li>
              <li><strong>The Bronx:</strong> High risk/reward, tough neighborhood</li>
              <li><strong>Staten Island:</strong> Low crime, good property deals</li>
            </ul>
          </div>
          <div className="info-card">
            <h4>Risk Levels</h4>
            <ul>
              <li><span style={{ color: 'var(--success-color)' }}>Low Risk:</span> Minimal chance of police encounter</li>
              <li><span style={{ color: 'var(--warning-color)' }}>Medium Risk:</span> Moderate chance of police encounter</li>
              <li><span style={{ color: 'var(--error-color)' }}>High Risk:</span> High chance of police encounter</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Travel;
